import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trophy, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export const ConversionModal = ({ isOpen, onClose, lead, onConfirm }) => {
  const [step, setStep] = useState(1); // 1: Datos Cliente, 2: Datos Póliza
  const [loading, setLoading] = useState(false);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    // Cliente
    name: '',
    email: '',
    phone: '',
    id_number: '', // RFC/DNI
    dob: '',
    
    // Póliza
    policy_number: '',
    insurer: '',
    policy_type: '',
    annual_premium: '',
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (lead) {
      setFormData(prev => ({
        ...prev,
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        id_number: lead.id_number || '',
        annual_premium: lead.value || '',
        policy_type: lead.product || ''
      }));
    }
  }, [lead]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (!formData.name) return toast.error("El nombre es obligatorio");
    // Podríamos añadir validación de duplicados aquí en el futuro
    setStep(2);
  };

  const handleSubmit = async () => {
    // Validar paso 2
    if (!formData.policy_number) return toast.error("El número de póliza es obligatorio");
    if (!formData.insurer) return toast.error("La aseguradora es obligatoria");
    
    setLoading(true);
    try {
      await onConfirm(formData);
      toast.success("¡Venta registrada exitosamente!");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Error al registrar la venta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-green-100 p-2 rounded-full">
              <Trophy className="h-6 w-6 text-green-600" />
            </div>
            <DialogTitle className="text-xl text-green-700">¡Lead Ganado!</DialogTitle>
          </div>
          <DialogDescription>
            Convierte a <strong>{lead?.name}</strong> en cliente y registra su primera póliza.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step === 1 ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Paso 1: Completar Datos del Cliente
              </h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="name">Nombre Completo *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} />
                  </div>
                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="id_number">RFC / DNI</Label>
                    <Input id="id_number" name="id_number" value={formData.id_number} onChange={handleChange} placeholder="Opcional" />
                  </div>
                  <div>
                    <Label htmlFor="dob">Fecha Nacimiento</Label>
                    <Input id="dob" name="dob" type="date" value={formData.dob} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Paso 2: Registrar Póliza Vendida
              </h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="policy_number">No. Póliza *</Label>
                    <Input id="policy_number" name="policy_number" value={formData.policy_number} onChange={handleChange} />
                  </div>
                  <div>
                     <Label htmlFor="start_date">Inicio Vigencia</Label>
                     <Input id="start_date" name="start_date" type="date" value={formData.start_date} onChange={handleChange} />
                  </div>
                </div>

                <div>
                  <Label htmlFor="insurer">Aseguradora *</Label>
                  <Input id="insurer" name="insurer" value={formData.insurer} onChange={handleChange} placeholder="Ej. GNP, AXA..." />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="policy_type">Ramo / Producto</Label>
                    <Select 
                      value={formData.policy_type} 
                      onValueChange={(val) => handleSelectChange('policy_type', val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Autos">Autos</SelectItem>
                        <SelectItem value="GMM">Gastos Médicos</SelectItem>
                        <SelectItem value="Vida">Vida</SelectItem>
                        <SelectItem value="Hogar">Hogar</SelectItem>
                        <SelectItem value="Daños">Daños</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="annual_premium">Prima Anual ($)</Label>
                    <Input id="annual_premium" name="annual_premium" type="number" value={formData.annual_premium} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          {step === 2 ? (
            <Button variant="ghost" onClick={() => setStep(1)}>
              Atrás
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
          )}

          {step === 1 ? (
            <Button onClick={validateStep1} className="bg-indigo-600 hover:bg-indigo-700">
              Siguiente: Datos Póliza
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white gap-2">
              <CheckCircle size={16} />
              {loading ? 'Procesando...' : 'Confirmar Venta'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
